import React, {Component} from "react"
import PropTypes from "prop-types"
import {Row, Col} from "react-bootstrap"
import Dropzone from "react-dropzone"
import Papa from "papaparse"
import {get, isNull, has, flatten, isEmpty, isString} from "lodash"

import {ValidationButton, Input} from "@irati/ui/src/FormItem"
import ErrorText from "@irati/ui/src/ErrorText"
import SuccessText from "@irati/ui/src/SuccessText"
import AddRemoveInputList from "@irati/ui/src/AddRemoveInputList"
import Loading from "@irati/ui/src/Loading"
import adminTracking from "@irati-tracking/admin"
import authentication from "@irati/auth"
import {codeSchema, universalCodeSchema} from "@irati/admin/helpers/validation/schema"
import {FormattedMessage, injectIntl} from "@irati/utils/lang"

import WelcomeMessage from "./WelcomeMessage"
import {CODE, SINGLE_GLOBAL_CODE, MULTIPLE_CODES, WELCOME_MESSAGE} from "../../../../constants/selfSignUp"
import SignUpOption from "./SignUpOption"

import {MarginBottom, StyledCSVLink, StyledHR, StyledLabel3, CodeSelectionMenu, ImportCodes, Flex} from "./styled/SecretCode"

class SecretCode extends Component {
  static propTypes = {
    show: PropTypes.bool.isRequired,
    submit: PropTypes.object.isRequired,
    selfSignUp: PropTypes.object.isRequired,
    onSelect: PropTypes.func.isRequired,
    onChange: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    formatMessage: PropTypes.func.isRequired
  }

  constructor(props) {
    super(props)
    this.state = this.emptyState()
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.show !== nextProps.show) {
      this.setState(this.emptyState())
    }
  }

  emptyState = () => {
    const isSingleGlobalCode = get(this.props.selfSignUp, `config.${CODE}.logic`, "KEEP") === "KEEP"
    return {
      failedKeys: [],
      csvCodes: [],
      csvCodesExist: this.areMultipleCodesAlreadySaved(),
      singleGlobalCode: isSingleGlobalCode ? get(this.props.selfSignUp, `config.${CODE}.keys`) : null,
      codes: [null],
      codeSelect: isSingleGlobalCode ? SINGLE_GLOBAL_CODE : MULTIPLE_CODES
    }
  }

  handleCodeSelect = (codeSelect) => {
    this.setState({codeSelect, codes: [null]})
  }

  updateCodes = (codes) => {
    this.setState({codes})
  }

  updateSingleCode = (singleGlobalCode) => {
    this.setState({singleGlobalCode})
    const config = get(this.props.selfSignUp, "config", {})
    config[CODE] = {
      keys: [singleGlobalCode],
      usedKeys: null,
      logic: "KEEP"
    }
    this.props.onChange("config", config)
    this.props.onChange("method", CODE)
  }

  onSubmitCodes = () => {
    const {codes, codeSelect} = this.state

    if (codeSelect === MULTIPLE_CODES) {
      const config = get(this.props.selfSignUp, "config", {})
      const codesWithCSVCodes = [...codes, ...this.state.csvCodes].filter((code) => !isNull(code))
      config[CODE] = {
        keys:
          has(config, `${CODE}.keys`) && get(config, `${CODE}.logic`) !== "KEEP"
            ? [...config[CODE].keys, ...codesWithCSVCodes]
            : codesWithCSVCodes,
        usedKeys: get(config, `${CODE}.usedKeys`, null),
        logic: "POP"
      }
      this.props.onChange("config", config)
    }
    adminTracking.enableSSU()
    this.props.onChange("method", CODE)
    this.props.onSubmit()
  }

  handleDrop = (acceptedFile) => {
    const {formatMessage} = this.props
    this.setState({failedKeys: false})
    if (acceptedFile) {
      Papa.parse(acceptedFile[0], {
        complete: (results, file) => {
          let keys = flatten(results.data).filter((value) => !isEmpty(value))
          const failedKeys = []
          keys = keys.reduce((newKeys, key) => {
            if (codeSchema().isValidSync(key)) {
              return [...newKeys, key]
            }
            failedKeys.push(key)
            return newKeys
          }, [])

          this.setState({
            failedKeys,
            savedMessage: formatMessage({id: "admin.components.secretCode.savedMessage"}, {filename: file.name}),
            csvCodes: keys,
            codes: this.state.codes.map((code) => {
              return isString(code) && code.trim() === "" ? null : code
            }),
            csvCodesExist: true
          })
        }
      })
    }
  }

  updateWelcomeMessage = (welcomeMessage) => {
    this.props.onChange("welcomeMessage", welcomeMessage)
  }

  onSubmit = () => {
    this.props.onChange("method", CODE)
    this.props.onSubmit()
  }

  areMultipleCodesAlreadySaved = () => {
    return get(this.props.selfSignUp, `config.${CODE}.logic`) === "POP" && get(this.props.selfSignUp, `config.${CODE}.keys`, []).length > 0
  }

  getDataForCSV = () => {
    const usedKeys = get(this.props.selfSignUp, `config.${CODE}.usedKeys`, [])
    const {formatMessage} = this.props
    return get(this.props.selfSignUp, `config.${CODE}.keys`).map((code) => {
      const csvRow = usedKeys.includes(code) ? `${code}: ${formatMessage({id: "admin.components.secretCode.codeHasBeenUsed"})}` : code
      return [csvRow]
    })
  }

  render() {
    const {codeSelect, codes, failedKeys, savedMessage, singleGlobalCode, csvCodesExist} = this.state
    const signUpLink = `${authentication.userTenant().subdomain}.perkbox.com/signup`
    const {formatMessage} = this.props
    return (
      <SignUpOption
        title={formatMessage({id: "admin.components.secretCode.signUpOption.title"})}
        description={formatMessage({id: "admin.components.secretCode.signUpOption.description"}, {signUpLink})}
        onClick={() => this.props.onSelect(CODE)}
        active={this.props.show}>
        <CodeSelectionMenu>
          <Flex>
            <StyledLabel3
              key="Create a universal code"
              onClick={() => this.handleCodeSelect(SINGLE_GLOBAL_CODE)}
              selected={codeSelect === SINGLE_GLOBAL_CODE}>
              <FormattedMessage id="admin.components.secretCode.codeSelectionMenu.label.universalCode.text" />
            </StyledLabel3>
            <StyledLabel3
              key="Create single-use codes"
              selected={codeSelect === MULTIPLE_CODES}
              onClick={() => this.handleCodeSelect(MULTIPLE_CODES)}>
              <FormattedMessage id="admin.components.secretCode.codeSelectionMenu.label.singleUseCodes.text" />
            </StyledLabel3>
          </Flex>
          <StyledHR />
        </CodeSelectionMenu>
        {codeSelect === SINGLE_GLOBAL_CODE && (
          <Row>
            <Col xs={12}>
              <p>
                <FormattedMessage id="admin.components.secretCode.title.text.singleGlobalCode" />
              </p>
            </Col>
            <Input
              name="secretCode"
              hint={formatMessage({id: "admin.components.secretCode.input.hint"})}
              label={formatMessage({id: "admin.components.secretCode.input.label"})}
              onChange={this.updateSingleCode}
              value={singleGlobalCode}
              validation={universalCodeSchema()} />
          </Row>
        )}
        {codeSelect === MULTIPLE_CODES && (
          <MarginBottom>
            {this.areMultipleCodesAlreadySaved() && (
              <StyledCSVLink data={this.getDataForCSV()} filename="perkbox-single-sign-up-codes.csv">
                <FormattedMessage id="admin.components.secretCode.csvLink.signUpCodes" />
              </StyledCSVLink>
            )}
            <p>
              <FormattedMessage id="admin.components.secretCode.title.text.codeOnlyOnce" />
            </p>
            <AddRemoveInputList
              update={this.updateCodes}
              inputs={codes}
              label={formatMessage({id: "admin.components.secretCode.addRemoveInputList.label"})}
              addButtonText={formatMessage({id: "admin.components.secretCode.addRemoveInputList.buttonText"})}
              validation={codeSchema(!csvCodesExist)} />
            <p>
              <FormattedMessage id="admin.components.secretCode.title.text.singleCodesInCSV" />
            </p>
            <Dropzone name="codes" className="selfSignUpCodes" accept=".csv" onDrop={this.handleDrop}>
              <ImportCodes full onClick={this.handleDrop}>
                <FormattedMessage id="admin.components.secretCode.importCodes.text" />
              </ImportCodes>
            </Dropzone>
            {savedMessage && <SuccessText>{savedMessage}</SuccessText>}
            {failedKeys.length > 0 &&
              failedKeys.map((key, index) => (
                <ErrorText key={index}>
                  <FormattedMessage id="admin.components.secretCode.errorText" values={{key}} />
                </ErrorText>
              ))}
          </MarginBottom>
        )}
        <WelcomeMessage
          updateWelcomeMessage={this.updateWelcomeMessage}
          welcomeMessage={get(this.props.selfSignUp, "welcomeMessage", formatMessage({id: WELCOME_MESSAGE}))} />
        <Loading load={this.props.submit} useLoader={false} />
        <ValidationButton primary full large load={this.props.submit} onClick={this.onSubmitCodes}>
          <FormattedMessage id="admin.components.secretCode.validationButton.save" />
        </ValidationButton>
      </SignUpOption>
    )
  }
}

export default injectIntl(SecretCode)
